import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MapPin,
  Globe,
  Building2,
  Hexagon,
  GitBranch,
  User,
  Ruler,
  Phone,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@/lib/store";
import States from "./States";
import Cities from "./Cities";
import Schools from "./Schools";
import Zones from "./Zones";
import Branches from "./Branches";
import Roles from "./Roles";
import Ranges from "./Ranges";
import MobileNumber from "./mobileNumbers/MobileNumbers";
import BillingDates from "./billingCycle/BillingDates";

type MasterTab = "states" | "cities" | "schools" | "zones" | "branches" | "roles" | "ranges" | "mobilenumbers" | "billingCycle";

const allTabs: Array<{ id: MasterTab; screen: string; label: string; icon: React.ReactNode }> = [
  { id: "states",   screen: "states",   label: "States",   icon: <MapPin className="w-4 h-4" /> },
  { id: "cities",   screen: "cities",   label: "Districts",   icon: <Globe className="w-4 h-4" /> },
  { id: "branches", screen: "branches", label: "Branches", icon: <GitBranch className="w-4 h-4" /> },
  { id: "schools",  screen: "schools",  label: "Schools",  icon: <Building2 className="w-4 h-4" /> },
  { id: "zones",    screen: "zones",    label: "Zones",    icon: <Hexagon className="w-4 h-4" /> },
  { id: "roles",    screen: "roles",    label: "Roles",    icon: <User className="w-4 h-4" /> },
  // { id: "ranges",   screen: "ranges",   label: "Ranges",   icon: <Ruler className="w-4 h-4" /> },
  { id: "mobilenumbers",   screen: "mobile numbers",   label: "Mobile Numbers",   icon: <Phone className="w-4 h-4" /> },
  { id: "billingCycle",   screen: "billing cycle",   label: "Billing Cycle",   icon: <Phone className="w-4 h-4" /> },
];

export default function Masters() {
  const { loaded: permissionsLoaded, data: permissions } = useSelector((state: RootState) => state.permissions);
  const [searchParams, setSearchParams] = useSearchParams();
  // Filter tabs: show while loading, or if the key exists in permissions and Can_View !== false
  const visibleTabs = allTabs.filter((tab) =>
    !permissionsLoaded || (tab.screen in permissions && permissions[tab.screen]?.Can_View !== false)
  );

  const [activeTab, setActiveTab] = useState<MasterTab | null>(null);

  // Sync tab from URL param or default to first visible tab
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') as MasterTab | null;
    if (tabFromUrl && visibleTabs.find((t) => t.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else if (visibleTabs.length > 0 && (!activeTab || !visibleTabs.find((t) => t.id === activeTab))) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [searchParams.get('tab'), visibleTabs.map((t) => t.id).join(",")]);

  const handleTabChange = (tab: MasterTab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  const renderComponent = () => {
    switch (activeTab) {
      case "states":   return <States />;
      case "cities":   return <Cities />;
      case "schools":  return <Schools />;
      case "zones":    return <Zones />;
      case "branches": return <Branches />;
      case "roles":    return <Roles />;
      case "ranges":   return <Ranges />;
      case "mobilenumbers":   return <MobileNumber />
      case "billingCycle":   return <BillingDates />
      default:         return null;
    }
  };

  if (visibleTabs.length === 0) {
    return (
      <div className="text-center py-20 text-gray-400">
        <p className="font-medium">You don't have access to any master screens.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="rounded-2xl shadow-lg overflow-hidden" style={{ background: 'linear-gradient(135deg, #2d3440 0%, #535c6b 100%)' }}>
        <div className="px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-extrabold text-white tracking-tight">Masters Management</h2>
            <p className="text-white/60 text-sm mt-0.5">States, cities, schools, zones, branches & more</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-mlb-gold/20 border border-mlb-gold/30 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-mlb-gold" />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex-1 relative px-2 py-3 transition-all duration-200 flex flex-col items-center gap-1.5 border-b-2 ${
                activeTab === tab.id
                  ? "border-brand-500 bg-brand-500"
                  : "border-transparent bg-white hover:bg-gray-50"
              }`}
            >
              <div className={`p-1.5 rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-mlb-gold text-brand-700 shadow-sm"
                  : "bg-gray-100 text-gray-500"
              }`}>
                {tab.icon}
              </div>
              <span className={`text-xs font-bold transition-colors ${
                activeTab === tab.id ? "text-white" : "text-gray-500"
              }`}>
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div>{renderComponent()}</div>
      </div>
    </div>
  );
}
